import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AgendaelementId, MøteId} from "./ressurser/generertNg2HttpKlient";
import {Injectable} from "@angular/core";
import {AgendaelementModellDLM, SakDatatjeneste} from "./datalager/SakDatatjeneste";
import {map} from "rxjs/operators";
import {sakStarttidspunktEllerMøtestartDersomNil} from "../modell/sakModellHjelpere";
import {BehaviorSubject, combineLatestWith, finalize, Observable} from "rxjs";
import {lesMøtedato2} from "../modell/møteModellHjelpere";
import {erIkkeTomListe, erTomListe, første, reduserTilSum} from "../støttefunksjoner/lister";
import {ZonedDateTimePeriode} from "../støttefunksjoner/zonedDateTimePerioder";
import {erIkkeNil, erNil} from "../støttefunksjoner/nilsjekk";
import {durationFraPeriode, durationTilMinutter, erSammeDagZonedDateTime, erZonedDateTimeSenereEnn, varighetIMinutter} from "../støttefunksjoner/temporal";
import {Sakstidspunkt as AjsSakstidspunkt} from "../../felles/tjenester/ressurser/SakRessurs";
import {Temporal} from "temporal-polyfill";
import {zonedDateTimeSomDate} from "felles-ng2/støttefunksjoner/temporalTilDateKonverteringer";
import {verifiserErIkkeTomListe} from "felles-ng2/støttefunksjoner/antakelser";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import Duration = Temporal.Duration;
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('Sakstidspunkter');


export type SakstidspunkterMap = ReadonlyMap<AgendaelementId, Readonly<Sakstidspunkt>>;
export type SaksinfoForSakstidspunktberegning = Pick<AgendaelementModellDLM, 'id' | 'varighet' | 'dag' | 'starttidspunkt'>;

type Innslag = { tidspunkterBeregning: SakstidspunkterBeregning, teller: number }

/**
 * Tjeneste som gir tilgang til beregnede sakstidspunkter for møte.
 * Merk at vi forventer at saker knyttet til møtet man forespør sakstidspunkter for er tilgjengelig i lokalt datalager i {@link SakDatatjeneste}.
 */
@Injectable({providedIn: 'root'})
export class Sakstidspunkter {

    private aktive = new Map<MøteId, Innslag>()

    constructor(private sakDatatjeneste: SakDatatjeneste) {
    }

    /** Returnerer sakstidspunkter-beregning for angitt møteId.
     * Returnert instans vil blir delt mellom alle som kaller denne metoden for samme møteId,
     * dermed blir eventuelle overstyringer som registeres via returnert {@link SakstidspunkterBeregning} være
     * synlige for alle som har forespurt sakstidspunkter-beregning for angitt møteId.*/
    forMøte(møteId: MøteId): SakstidspunkterBeregning {
        if (!this.aktive.has(møteId))
            this.aktive.set(møteId, {tidspunkterBeregning: new SakstidspunkterBeregning(møteId, this.sakDatatjeneste, møteId => this.terminer(møteId)), teller: 0});
        const innslag = this.aktive.get(møteId)!;
        innslag.teller++;
        debug(`returnerer SakstidspunkterBeregning, teller: ${innslag.teller}`)
        return innslag.tidspunkterBeregning;
    }

    /** Returnerer en strøm av {@link SakstidspunkterInformasjon} for angitt møte.
     * Delegerer til {@link SakstidspunkterBeregning#tidspunkterSomObserverbar} */
    forMøteSomObserverbar(møteId: MøteId): Observable<SakstidspunkterInformasjon> {
        return this.forMøte(møteId).tidspunkterSomObserverbar()
          .pipe(finalize(() => this.terminer(møteId)));
    }

    //private hjelpemetoder

    private terminer(møteId: MøteId) {
        const innslag = this.aktive.get(møteId);
        if (erNil(innslag)) return;
        innslag.teller--;
        if (innslag.teller <= 0) this.aktive.delete(møteId);
        debug(`SakstidspunkterBeregning instans terminert, teller: ${innslag.teller}`)
    }
}

/** klasse som gir tilgang til sakstidspunktberegninger for et gitt møte */
export class SakstidspunkterBeregning {
    private overstyringerSub = new BehaviorSubject<SaksinfoForSakstidspunktberegning[]>([])
    private overstyringerForEnkeltsaker = new Map<AgendaelementId, SaksinfoForSakstidspunktberegning>();
    private overstyringerForAlleSaker: SaksinfoForSakstidspunktberegning[] = [];
    private saker: AgendaelementModellDLM[] = [];

    constructor(private møteId: MøteId, private sakDatatjeneste: SakDatatjeneste, private vedTerminer: (møteId: MøteId) => void) {
    }

    /** Returnerer en observerbar som leverer {@link SakstidspunkterInformasjon}
     * når underliggende saker endres eller saksinformasjon overstyres vhja
     * feks {@link #medOverstyringForSak} */
    tidspunkterSomObserverbar(): Observable<SakstidspunkterInformasjon> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(this.møteId)
          .pipe(
            combineLatestWith(this.overstyringerSub),
            map(([saker, overstyrte]) => {
                this.saker = saker;
                return this.beregnSakstidspunkter()
            }));
    }

    forSaker(saker: AgendaelementModellDLM[]): SakstidspunkterInformasjon {
        this.saker = saker;
        return this.beregnSakstidspunkter();
    }

    /** Overstyrere informasjon om alle saker, dvs av saker angitt via {@link #forSaker} ignorere fullstendig.
     * Eventuelle overstyringer angitt via {@link #medOverstyringForSak} ignoreres også. */
    medOverstyringerForAlleSaker(overstyringer: SaksinfoForSakstidspunktberegning[]): SakstidspunkterInformasjon {
        debug('medOverstyringerForAlleSaker', overstyringer)
        this.overstyringerForAlleSaker = [...overstyringer];
        this.overstyringerForEnkeltsaker.clear();
        this.overstyringerSub.next([...this.overstyringerForAlleSaker]);
        return this.beregnSakstidspunkter();
    }

    /** Overstyrer informasjon om enkelt-sak.
     * Eventuelle overstyringer angitt via {@link #medOverstyringerForAlleSaker} ignoreres */
    medOverstyringForSak(overstyring: SaksinfoForSakstidspunktberegning): SakstidspunkterInformasjon {
        debug('medOverstyring', overstyring)
        this.overstyringerForEnkeltsaker.set(overstyring.id, overstyring);
        this.overstyringerForAlleSaker = [];
        this.overstyringerSub.next([...this.overstyringerForEnkeltsaker.values()]);
        return this.beregnSakstidspunkter();
    }

    resetOverstyringer() {
        this.overstyringerForAlleSaker = [];
        this.overstyringerForEnkeltsaker.clear();
        this.overstyringerSub.next([]);
    }

    terminer() {
        this.vedTerminer(this.møteId);
    }

    // private hjelpemetoder

    private beregnSakstidspunkter(): SakstidspunkterInformasjon {
        if (erTomListe(this.saker) && erTomListe(this.overstyringerForAlleSaker)) return SakstidspunkterInformasjon.tom();
        verifiserErIkkeTomListe(this.saker, 'har du glemt å kalle forSaker([liste med saker])?')
        if (erIkkeTomListe(this.overstyringerForAlleSaker) || this.overstyringerForEnkeltsaker.size > 0)
            debug('saksliste blir overstyrt overstyringerForAlleSaker:%O overstyringerForEnkeltsaker:%O saker:%O', this.overstyringerForAlleSaker, [...this.overstyringerForEnkeltsaker.values()], this.saker);
        const møte = this.saker[0].moete;
        const sakerBruktIBeregning = erIkkeTomListe(this.overstyringerForAlleSaker) ?
          this.overstyringerForAlleSaker :
          this.saker.map(sak => this.erstattMedPotensiellEnkeltsakOverstyring(sak));
        return new SakstidspunkterInformasjon(lesMøtedato2(møte), sakerBruktIBeregning);
    }

    private erstattMedPotensiellEnkeltsakOverstyring(sak: AgendaelementModellDLM) {
        return this.overstyringerForEnkeltsaker.get(sak.id) ?? sak;
    }
}

/**
 * Beregner sakstidspunkter for saker i et møte.
 * <pre>
 * For enkeltdagsmøter blir sakens varighet blir beregnet dersom ikke angitt på saken såfremt minst én sak har varighet angitt,
 * varighet blir da satt til udisponert tid / antall saker uten angitt varighet.
 *
 * For flerdagsmøter er vi avhengige av at alle saker har varighet satt for at start/slutt dato skal kunne beregnes.
 *
 * Merk: pga skjerming er ikke nødvendigvis alle saker inkludert i angitte saker, i slike tilfeller vil beregningen av
 * tidspunkter kunne bli feil.
 * </pre>
 */
export class SakstidspunkterInformasjon {
    flerdagsmoete: boolean;
    ingenVarigheterSatt: boolean;
    alleHarVarighetSatt: boolean;
    sakstidspunkter: SakstidspunkterMap;

    /*
    @todo vurder å flytte hit
    brukes i MøteRessurs.beregnetMøtevarighet2:
      angittVarighet: angittVarighet,
            beregnetVarighet: beregnetVarighet,
            justertVarighet: beregnetErEtterSlutt ? beregnetVarighet : angittVarighet,
            justertSlutt: beregnetErEtterSlutt ? beregnetSlutt.toDate() : møte.dato.slutt,
            justertErEtterSlutt: beregnetErEtterSlutt,
            differanse: beregnetSlutt.twix(møte.dato.slutt).length("minutes") * -1
     */
    static tom(): SakstidspunkterInformasjon {
        return new SakstidspunkterInformasjon(new ZonedDateTimePeriodeLukket(Temporal.Now.zonedDateTimeISO(), Temporal.Now.zonedDateTimeISO()), []);
    }

    constructor(public readonly møtedato: ZonedDateTimePeriodeLukket, saker: SaksinfoForSakstidspunktberegning[]) {
        debug('sakstidspunkter kalt med saker', saker)
        const antallSakerMedVarighetAngitt = saker.filter(sak => erIkkeNil(sak.varighet)).length;
        this.flerdagsmoete = møtedato.antallDager() > 1;
        this.ingenVarigheterSatt = antallSakerMedVarighetAngitt == 0;
        this.alleHarVarighetSatt = antallSakerMedVarighetAngitt == saker.length;
        if (this.flerdagsmoete && this.alleHarVarighetSatt)
            this.sakstidspunkter = this.flerdagsmøteAlleSakerHarVarighetAngitt(saker);
        else if (this.flerdagsmoete)
            this.sakstidspunkter = this.flerdagsmøteNoenSakerHarIkkeVarighetAngitt(saker);
        else if (this.ingenVarigheterSatt)
            this.sakstidspunkter = this.enkeltdagsmøteMedIngenSakerHarVarighetAngitt(saker);
        else
            this.sakstidspunkter = this.enkeltdagsmøteNoenSakerHarVarighetAngitt(saker);
    }

    harForSak(id: AgendaelementId): boolean {
        return this.sakstidspunkter.has(id);
    }

    forSak(id: AgendaelementId): Sakstidspunkt {
        if (!this.harForSak(id))
            throw new Error(`sakstidspunkter inneholder ikke sak ${id}`);
        return this.sakstidspunkter.get(id)!;
    }

    sakstidspunkterSomListe(): Sakstidspunkt[] {
        return Array.from(this.sakstidspunkter.values());
    }

    /** Sann dersom det finnes saker på angitt dag */
    harSakerPåDag(dag: ZonedDateTime): boolean {
        return this.sakerPåDag(dag).length > 0;
    }

    /** Returnerer sakstidspunkt-informasjon for aller saker på angitt dag */
    sakerPåDag(dag: ZonedDateTime): Sakstidspunkt[] {
        return this.sakstidspunkterSomListe()
          .filter(sakstidspunkt => erSammeDagZonedDateTime(sakstidspunkt.start, dag))
    }

    /** Returnerer starttidspunktet for møtet på angitt dag.
     * For enkeltdagsmøter returnerer vi alltid møtedato.start.
     * For flerdagsmøter returnerer vi {@link Sakstidspunkt#start} for første sak på angitt dag,
     * merk at dersom det ikke finnes saker på angitt dag returneres møtedato.start */
    sakerPåDagStart(dag: ZonedDateTime): ZonedDateTime {
        return this.flerdagsmoete ?
          første(this.sakerPåDag(dag))?.start ?? this.møtedato.start :
          this.møtedato.start
    }

    /** Returnerer varigheten av møte for angitt dag.
     * For enkeltdagsmøter returnerer vi alltid møtedato.
     * For flerdagsmøter returnerer vi summen av varighetene til sakene som behandles på angitt dag. */
    sakerPåDagVarighet(dag: ZonedDateTime): Duration {
        return this.flerdagsmoete ?
          this.sakerPåDag(dag).reduce((sum, saksinfo) => sum.add(saksinfo.varighet!), new Duration(0)) :
          durationFraPeriode(this.møtedato);
    }
    
    /** Sann dersom det finnes en sak skal behandles i intervallet nå +/- en time.
     * For flerdagsmøter benyttes beregnet møtevarighet for den aktuelle dagen dersom varighet er satt på alle saker,
     * eller benyttet møtets start/slutt */
    harSakSomBehandlesNåPlussMinusEnTime(nå: ZonedDateTime):boolean{
        const nåPlussMinusEnTime = new ZonedDateTimePeriodeLukket(nå.subtract({hours: 1}), nå.add({hours: 1}));
        let anvendtMøtedato = this.møtedato;

        //for flerdagsmøter der varighet er satt på alle saker prøver vi å finne varigheten av saken på den aktuelle dagen (nå)
        if (this.flerdagsmoete && this.alleHarVarighetSatt) {
            const møtestart = this.sakerPåDagStart(nå);
            let beregnetMøteslutt = møtestart.add(this.sakerPåDagVarighet(nå));
            //benytt seneste av beregnetMøteslutt og møte.slutt når vi ser på siste dag i møtet
            if (erSammeDagZonedDateTime(beregnetMøteslutt, this.møtedato.slutt) && erZonedDateTimeSenereEnn(this.møtedato.slutt, beregnetMøteslutt))
                beregnetMøteslutt = this.møtedato.slutt;
            anvendtMøtedato = new ZonedDateTimePeriodeLukket(møtestart, beregnetMøteslutt);
        }

        // Er vi innenfor tidsvinduet (1 time før til 1 time etter nå)?
        return anvendtMøtedato.overlapper(nåPlussMinusEnTime);
    }

    //private hjelpemetoder

    private flerdagsmøteAlleSakerHarVarighetAngitt(saker: SaksinfoForSakstidspunktberegning[]): SakstidspunkterMap {
        let forrigeSak: SaksinfoForSakstidspunktberegning | null = null;
        let starttidspunkt = this.møtedato.start;
        let sakstidspunkter = saker
          .map((sak, indeks) => {
              //dersom forrige sak er første sak i dag, les starttidspunkt fra sak
              //med unntak av første sak i første dag - som ikke vil ha starttidspunkt satt
              if (forrigeSak?.dag != sak.dag && indeks > 0) starttidspunkt = sakStarttidspunktEllerMøtestartDersomNil(sak, this.møtedato, indeks);
              let tidspunkt = Sakstidspunkt.flerdagsmøteMedTidspunkt(sak, starttidspunkt, forrigeSak);
              starttidspunkt = tidspunkt.slutt!;
              forrigeSak = sak;
              return tidspunkt
          })
          .reduce((gruppert, tidspunkt) => gruppert.set(tidspunkt.sakId, tidspunkt), new Map<AgendaelementId, Sakstidspunkt>());
        debug('sakstidspunkter returnerer', sakstidspunkter)
        return sakstidspunkter;
    }

    private flerdagsmøteNoenSakerHarIkkeVarighetAngitt(saker: SaksinfoForSakstidspunktberegning[]): SakstidspunkterMap {
        let forrigeSak: SaksinfoForSakstidspunktberegning | null = null;
        let sakstidspunkter = saker
          .map(sak => {
              let tidspunkt = Sakstidspunkt.flerdagsmøteUtenTidspunkt(sak, this.ingenVarigheterSatt, forrigeSak, this.møtedato);
              forrigeSak = sak;
              return tidspunkt;
          })
          .reduce((gruppert, tidspunkt) => gruppert.set(tidspunkt.sakId, tidspunkt), new Map<AgendaelementId, Sakstidspunkt>());
        debug('sakstidspunkter returnerer', sakstidspunkter)
        return sakstidspunkter;
    }

    private enkeltdagsmøteMedIngenSakerHarVarighetAngitt(saker: SaksinfoForSakstidspunktberegning[]): SakstidspunkterMap {
        let sakstidspunkter = saker
          .map(sak => Sakstidspunkt.utenTidspunkt(sak.id))
          .reduce((gruppert, tidspunkt) => gruppert.set(tidspunkt.sakId, tidspunkt), new Map<AgendaelementId, Sakstidspunkt>());
        debug('sakstidspunkter returnerer', sakstidspunkter)
        return sakstidspunkter;
    }

    private enkeltdagsmøteNoenSakerHarVarighetAngitt(saker: SaksinfoForSakstidspunktberegning[]): SakstidspunkterMap {
        //ikke flerdagsmøte og minst én sak med varighet
        const sumVarigheter = saker.map(sak => sak.varighet ?? 0).reduce(...reduserTilSum());
        let antallSakerUtenVarighetAngitt = saker.filter(sak => erNil(sak.varighet)).length;
        let stdSaksvarighet = antallSakerUtenVarighetAngitt > 0 ? Math.floor((this.møtedato.antallMinutter() - sumVarigheter) / antallSakerUtenVarighetAngitt) : 0;
        if (stdSaksvarighet < 0) stdSaksvarighet = 0;

        let starttidspunkt = this.møtedato.start;
        let sakstidspunkter = saker
          .map(sak => {
              const tidspunkt = Sakstidspunkt.medTidspunkt(sak, stdSaksvarighet, starttidspunkt)
              starttidspunkt = tidspunkt.slutt!;
              return tidspunkt;
          })
          .reduce((gruppert, tidspunkt) => gruppert.set(tidspunkt.sakId, tidspunkt), new Map<AgendaelementId, Sakstidspunkt>());
        debug('sakstidspunkter returnerer', sakstidspunkter)
        return sakstidspunkter;
    }
}

export class Sakstidspunkt {

    /** for bruk ved enkeltdagsmøter når ingen saker har varighet satt */
    static utenTidspunkt(sakId: AgendaelementId) {
        const instans = new Sakstidspunkt(sakId);
        instans.flerdagsmoete = false;
        instans.foersteSakPaaDenneDag = false;
        instans.ingenVarigheterSatt = true;
        instans.varighetBeregnet = false;
        instans.varighetAngitt = false;
        instans.varighet = null;
        instans.start = null
        instans.slutt = null
        return instans;
    }

    /** for bruk ved enkeltdagsmøter når noen eller alle sakene har varighet satt */
    static medTidspunkt(sak: SaksinfoForSakstidspunktberegning, standardVarighet: number, starttidspunkt: ZonedDateTime) {
        const instans = new Sakstidspunkt(sak.id);
        instans.flerdagsmoete = false;
        instans.foersteSakPaaDenneDag = false;
        instans.ingenVarigheterSatt = false;
        instans.varighetBeregnet = erNil(sak.varighet);
        instans.varighetAngitt = erIkkeNil(sak.varighet);
        instans.varighet = varighetIMinutter(instans.varighetBeregnet ? standardVarighet : sak.varighet);
        instans.start = starttidspunkt;
        instans.slutt = starttidspunkt.add(instans.varighet!);
        return instans;
    }

    /** for bruk ved flerdagsmøter når alle saker har varighet angitt */
    static flerdagsmøteMedTidspunkt(sak: SaksinfoForSakstidspunktberegning, starttidspunkt: ZonedDateTime, forrigeSak: SaksinfoForSakstidspunktberegning | null) {
        const instans = new Sakstidspunkt(sak.id);
        instans.flerdagsmoete = true;
        instans.foersteSakPaaDenneDag = forrigeSak?.dag != sak.dag;
        instans.ingenVarigheterSatt = false;
        instans.enkelteVarigheterIkkeSatt = false;
        instans.varighetBeregnet = false;
        instans.varighetAngitt = erIkkeNil(sak.varighet);
        instans.dag = sak.dag ?? 0;
        instans.varighet = varighetIMinutter(sak.varighet);
        instans.start = starttidspunkt;
        instans.slutt = starttidspunkt.add(instans.varighet);
        return instans;
    }

    /** for bruk ved flerdagsmøter når IKKE alle saker har varighet angitt */
    static flerdagsmøteUtenTidspunkt(sak: SaksinfoForSakstidspunktberegning, ingenVarigheterSatt: boolean, forrigeSak: SaksinfoForSakstidspunktberegning | null, møtedato: ZonedDateTimePeriode) {
        const instans = new Sakstidspunkt(sak.id);
        instans.flerdagsmoete = true;
        instans.foersteSakPaaDenneDag = forrigeSak?.dag != sak.dag;
        instans.ingenVarigheterSatt = ingenVarigheterSatt;
        instans.enkelteVarigheterIkkeSatt = true;
        instans.varighetBeregnet = false;
        instans.varighetAngitt = erIkkeNil(sak.varighet);
        instans.dag = sak.dag ?? 0;
        instans.varighet = varighetIMinutter(sak.varighet);
        if (instans.foersteSakPaaDenneDag)
            instans.start = instans.dag == 0 ? møtedato.start : sakStarttidspunktEllerMøtestartDersomNil(sak, møtedato, instans.dag);
        instans.slutt = null;
        return instans;
    }


    constructor(sakId: AgendaelementId) {
        this.sakId = sakId;
        this.ingenVarigheterSatt = true;
        this.enkelteVarigheterIkkeSatt = true;
        this.varighetBeregnet = false;
        this.varighetAngitt = false;
        this.flerdagsmoete = false;
        this.foersteSakPaaDenneDag = false;
        this.dag = 0;
        this.varighet = null;
        this.start = null
        this.slutt = null
    }

    sakId: AgendaelementId;
    ingenVarigheterSatt: boolean;
    enkelteVarigheterIkkeSatt: boolean;
    varighetBeregnet: boolean;
    varighetAngitt: boolean;
    flerdagsmoete: boolean;
    /** sann dersom dette er første sak på denne dag i et flerdagsmøte */
    foersteSakPaaDenneDag: boolean;
    /** angir dag (0 basert), alltid 0 for enkeltdagsmøter */
    dag: number;
    /** Sakens varighet, null dersom enkeltdagsmøte og ingen saker har varighet angitt ({@link #ingenVarigheterSatt} = true).
     * For enkeltdagsmøter der minst én av sakene har varighet satt vil saker uten varighet satt får beregnet varighet.
     * For flerdagsmøter vil verdien være 0 for saker som ikke har varighet angitt */
    varighet: Duration | null;
    /** sakens starttidspunkt, beregnet basert på angitte varigheter til foregående saker.
     * Dersom ingen saker i møte har varighet satt vil start=null.
     * Ved flerdagsmøter vil start kun være angitt for første sak på den enkelte dag dersom ikke varighet er angitt på alle saker */
    start: ZonedDateTime | null;
    slutt: ZonedDateTime | null;

    /** Sann dersom start og slutt er ikke-null */
    harStartOgSlutt(): boolean {
        return erIkkeNil(this.start) && erIkkeNil(this.slutt);
    }

    /** Sann dersom angitt dato er mellom start (inklusive) og slutt (inklusive) */
    inneholder(dato: ZonedDateTime): boolean {
        if (!this.harStartOgSlutt()) return false;
        if (ZonedDateTime.compare(this.start!, dato) > 0) return false;
        if (ZonedDateTime.compare(this.slutt!, dato) < 0) return false;
        return true;
    }

    somAjsTidspunkter(): AjsSakstidspunkt {
        return {
            ...this,
            varighet: durationTilMinutter(this.varighet),
            start: zonedDateTimeSomDate(this.start),
            slutt: zonedDateTimeSomDate(this.slutt)
        }
    }
}
